/**
 * 프레임(표시방향 기준) → 뷰 픽셀 매핑 파라미터.
 *
 * cover = PreviewView FILL_CENTER / CSS object-fit: cover (B-3):
 *   scale = max(viewW/frameW, viewH/frameH), 중앙 정렬 오프셋(음수 = 잘림).
 */
export function computeViewMapping(frame, view, fit = 'cover') {
    if (frame.width <= 0 || frame.height <= 0 || view.width <= 0 || view.height <= 0) {
        throw new Error(`invalid sizes: frame=${frame.width}x${frame.height} view=${view.width}x${view.height}`);
    }
    const sx = view.width / frame.width;
    const sy = view.height / frame.height;
    const scale = fit === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
    return {
        scale,
        offsetX: (view.width - frame.width * scale) / 2,
        offsetY: (view.height - frame.height * scale) / 2,
    };
}
/** 정규화(0~1, 프레임 기준) 점 → 뷰 픽셀 좌표 (B-3 cover 공식) */
export function mapPointToView(pt, frame, view, fit = 'cover') {
    const m = computeViewMapping(frame, view, fit);
    return {
        x: pt.x * frame.width * m.scale + m.offsetX,
        y: pt.y * frame.height * m.scale + m.offsetY,
    };
}
export function mapQuadToView(corners, frame, view, fit = 'cover') {
    return corners.map((p) => mapPointToView(p, frame, view, fit));
}
/**
 * 버퍼(회전 전) 정규화 좌표 → 표시방향 정규화 좌표.
 *
 * rotationDegrees = 버퍼를 시계방향으로 몇 도 돌려야 표시방향이 되는가
 * (CameraX ImageInfo.rotationDegrees 와 동일 의미). 90/270 에서는 프레임
 * 가로세로가 뒤바뀌므로 소비자는 rotateSize() 로 크기도 함께 변환해야 한다.
 */
export function rotatePointToDisplay(pt, rotationDegrees) {
    switch (rotationDegrees) {
        case 0:
            return { x: pt.x, y: pt.y };
        case 90:
            return { x: 1 - pt.y, y: pt.x };
        case 180:
            return { x: 1 - pt.x, y: 1 - pt.y };
        case 270:
            return { x: pt.y, y: 1 - pt.x };
    }
}
/** rotatePointToDisplay 의 역변환 (표시방향 → 버퍼) */
export function rotatePointFromDisplay(pt, rotationDegrees) {
    switch (rotationDegrees) {
        case 0:
            return { x: pt.x, y: pt.y };
        case 90:
            return { x: pt.y, y: 1 - pt.x };
        case 180:
            return { x: 1 - pt.x, y: 1 - pt.y };
        case 270:
            return { x: 1 - pt.y, y: pt.x };
    }
}
export function rotateSize(size, rotationDegrees) {
    return rotationDegrees === 90 || rotationDegrees === 270
        ? { width: size.height, height: size.width }
        : { width: size.width, height: size.height };
}
export function rotateQuadToDisplay(corners, rotationDegrees) {
    return normalizeCornerOrder(corners.map((p) => rotatePointToDisplay(p, rotationDegrees)));
}
/**
 * 임의 순서의 4점을 회사 표준 [좌상, 우상, 우하, 좌하] 로 정렬.
 *
 * 중심 기준 각도로 순환 순서를 먼저 확정하고(축퇴 불가), 시작점만 bbox 좌상단
 * 최근접으로 고른다.
 *
 * ⚠ 종전 구현(TL=min(x+y), BR=max(x+y), TR=max(x−y), BL=min(x−y))은 **같은 점이
 * 두 자리에 배정돼 4점이 3점으로 무너졌다**. 축 극값이라 카드가 기울면 무너지는데,
 * 좌표가 프레임 정규화(0~1)라 9:16 프레임에서는 카드가 가로로 눌려 붕괴 구간이
 * 45도 ±15.6도까지 넓어진다 — **전 회전각의 34.8%**(정사각 좌표라면 45도 한 점).
 * 무너지면 라이브는 점 3개만 그려지고, 촬영은 isConvexQuad 의 중복 코너 검사에
 * 걸려 detected=false(테두리 인식 실패)로 끝난다.
 */
export function normalizeCornerOrder(corners) {
    const cx = (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4;
    const cy = (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4;
    // 화면 좌표(y 아래로 증가)에서 각도 오름차순 = 시계방향 순회 (TL→TR→BR→BL)
    const cyclic = [...corners].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));
    const minX = Math.min(...corners.map((p) => p.x));
    const minY = Math.min(...corners.map((p) => p.y));
    let start = 0;
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < 4; i++) {
        const d = Math.hypot(cyclic[i].x - minX, cyclic[i].y - minY);
        if (d < best) {
            best = d;
            start = i;
        }
    }
    return [cyclic[start], cyclic[(start + 1) % 4], cyclic[(start + 2) % 4], cyclic[(start + 3) % 4]];
}
/**
 * 볼록성 검사 (A-3 자기교차/나비넥타이 방어): 인접 변 외적 부호가 전부 동일해야 한다.
 * 자기교차 사각형은 면적이 작게 계산돼 fill 게이트를 "통과"하는 함정이 있으므로,
 * 크롭 전 반드시 이 검사로 거부한다. 중복 코너(변 길이 0)도 거부.
 */
export function isConvexQuad(corners) {
    const EPS = 1e-9;
    let sign = 0;
    for (let i = 0; i < 4; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        const c = corners[(i + 2) % 4];
        const abx = b.x - a.x;
        const aby = b.y - a.y;
        const bcx = c.x - b.x;
        const bcy = c.y - b.y;
        if (Math.hypot(abx, aby) < EPS)
            return false; // 중복 코너
        const cross = abx * bcy - aby * bcx;
        if (Math.abs(cross) < EPS)
            continue; // 일직선 — 판정 유보
        const s = Math.sign(cross);
        if (sign === 0)
            sign = s;
        else if (s !== sign)
            return false;
    }
    return sign !== 0;
}
//# sourceMappingURL=geometry.js.map